import React, { useCallback, useEffect, useMemo, useState } from 'react';

type UseSyncedLocalStorageOptions<T> = {
  deserialize?: (value: string) => T;
  serialize?: (value: T) => string;
};

const getChangeEventName = (key: string) => `easx:local-storage:${key}`;

const resolveValue = <T>(value: T | (() => T)) =>
  typeof value === 'function' ? (value as () => T)() : value;

export const useSyncedLocalStorage = <T>(
  key: string,
  initialValue: T | (() => T),
  options: UseSyncedLocalStorageOptions<T> = {},
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const deserialize = useMemo(
    () => options.deserialize || ((value: string) => JSON.parse(value) as T),
    [options.deserialize],
  );
  const serialize = useMemo(
    () => options.serialize || ((value: T) => JSON.stringify(value)),
    [options.serialize],
  );

  const readValue = useCallback(() => {
    const fallback = resolveValue(initialValue);

    try {
      const storedValue = localStorage.getItem(key);
      return storedValue === null ? fallback : deserialize(storedValue);
    } catch {
      return fallback;
    }
  }, [deserialize, initialValue, key]);

  const [value, setValue] = useState<T>(readValue);

  useEffect(() => {
    setValue(readValue());
  }, [readValue]);

  useEffect(() => {
    const syncValue = () => {
      setValue(readValue());
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === key) {
        syncValue();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(getChangeEventName(key), syncValue);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(getChangeEventName(key), syncValue);
    };
  }, [key, readValue]);

  const setSyncedValue = useCallback<React.Dispatch<React.SetStateAction<T>>>(
    (valueOrUpdater) => {
      setValue((currentValue) => {
        const nextValue =
          typeof valueOrUpdater === 'function'
            ? (valueOrUpdater as (currentValue: T) => T)(currentValue)
            : valueOrUpdater;

        localStorage.setItem(key, serialize(nextValue));
        window.dispatchEvent(new CustomEvent(getChangeEventName(key)));
        return nextValue;
      });
    },
    [key, serialize],
  );

  return [value, setSyncedValue];
};
